import {
  Badge,
  Box,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  Stack,
  Table,
  Text,
  Timeline,
} from '@mantine/core';
import { TRenewalHistory, useRenewalDetails } from '@/models/renewals';
import { semanticJurisdiction } from '@/lib/helpers';

function formatDate(date: string | null) {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function formatDateTime(date: string | null) {
  if (!date) return '-';
  return new Date(date).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(amount: number | null, currency: string | null) {
  if (amount == null || currency == null) return '-';
  return `${Number(amount).toFixed(2)} ${currency}`;
}

const STATUS_COLORS: Record<string, string> = {
  pending: 'blue',
  invoiced: 'orange',
  paid: 'green',
  cancelled: 'red',
};

function HistoryEntry({ entry }: { entry: TRenewalHistory }) {
  if (entry.action === 'create') {
    return (
      <Text fz="xs">
        <Text span fw={500}>
          {entry.changedByName}
        </Text>{' '}
        added {entry.positionId ? 'a position' : 'a record'}
      </Text>
    );
  }
  if (entry.action === 'delete') {
    return (
      <Text fz="xs">
        <Text span fw={500}>
          {entry.changedByName}
        </Text>{' '}
        removed {entry.positionId ? 'a position' : 'a record'}
      </Text>
    );
  }
  return (
    <Text fz="xs">
      <Text span fw={500}>
        {entry.changedByName}
      </Text>{' '}
      changed{' '}
      <Text span ff="monospace">
        {entry.field}
      </Text>{' '}
      from{' '}
      <Text span c="red">
        {JSON.stringify(entry.oldValue)}
      </Text>{' '}
      to{' '}
      <Text span c="green">
        {JSON.stringify(entry.newValue)}
      </Text>
    </Text>
  );
}

export default function RenewalDetailsModal({
  renewalId,
  opened,
  onClose,
}: {
  renewalId: number | null;
  opened: boolean;
  onClose: () => void;
}) {
  const { data, isLoading } = useRenewalDetails(renewalId ?? 0);

  const renewal = data?.renewal;
  const positions = data?.positions || [];
  const history = data?.history || [];

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        renewal ? (
          <Group gap="xs">
            <Text fw={600}>Renewal #{renewal.id}</Text>
            <Badge
              size="sm"
              color={STATUS_COLORS[renewal.status] || 'gray'}
              variant="light"
            >
              {renewal.status}
            </Badge>
          </Group>
        ) : (
          'Renewal Details'
        )
      }
      size="xl"
    >
      <Box pos="relative" mih={200}>
        <LoadingOverlay visible={isLoading} />
        {renewal && (
          <Stack gap="md">
            <Group grow>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Client
                </Text>
                <Text fz="sm" fw={500}>
                  {renewal.contactName}
                </Text>
                <Text fz="xs" c="dimmed">
                  {renewal.contactEmail}
                </Text>
              </Stack>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Proposal
                </Text>
                <Text fz="sm">{renewal.proposalName}</Text>
                <Text fz="xs" c="dimmed">
                  {semanticJurisdiction(renewal.jurisdiction)}
                </Text>
              </Stack>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Cycle
                </Text>
                <Text fz="sm">{renewal.cycleNumber}</Text>
              </Stack>
            </Group>

            <Divider />

            <Group grow>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Anchor Date
                </Text>
                <Text fz="sm">{formatDate(renewal.anchorDate)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Renewal Date
                </Text>
                <Text fz="sm">{formatDate(renewal.renewalDate)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Issue Date
                </Text>
                <Text fz="sm">{formatDate(renewal.issueDate)}</Text>
              </Stack>
              <Stack gap={2}>
                <Text fz="xs" c="dimmed">
                  Due Date
                </Text>
                <Text fz="sm">{formatDate(renewal.dueDate)}</Text>
              </Stack>
            </Group>

            {renewal.invoiceId && (
              <>
                <Divider />
                <Group grow>
                  <Stack gap={2}>
                    <Text fz="xs" c="dimmed">
                      Invoice
                    </Text>
                    <Text fz="sm">{renewal.invoiceNumber}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text fz="xs" c="dimmed">
                      Invoice Amount
                    </Text>
                    <Text fz="sm">
                      {formatAmount(
                        renewal.invoiceAmount,
                        renewal.invoiceCurrency,
                      )}
                    </Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text fz="xs" c="dimmed">
                      Invoice Status
                    </Text>
                    <Badge
                      size="sm"
                      color={
                        STATUS_COLORS[renewal.invoiceStatus || ''] || 'gray'
                      }
                      variant="light"
                    >
                      {renewal.invoiceStatus || '-'}
                    </Badge>
                  </Stack>
                  {renewal.originalDueDate && (
                    <Stack gap={2}>
                      <Text fz="xs" c="dimmed">
                        Original Due Date
                      </Text>
                      <Text fz="sm">{formatDate(renewal.originalDueDate)}</Text>
                    </Stack>
                  )}
                </Group>
                {renewal.isAggregated && (
                  <Badge size="sm" variant="light">
                    Aggregated: {renewal.aggregationLabel || 'Unlabeled'}
                  </Badge>
                )}
              </>
            )}

            <Divider label="Positions" labelPosition="left" />
            <Table striped withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Text fz="xs" fw={500}>
                      Service
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fz="xs" fw={500}>
                      Interval
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fz="xs" fw={500}>
                      Price
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fz="xs" fw={500}>
                      Offset
                    </Text>
                  </Table.Th>
                  <Table.Th>
                    <Text fz="xs" fw={500}>
                      Fine
                    </Text>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {positions.map((pos) => (
                  <Table.Tr key={pos.id}>
                    <Table.Td>
                      <Text fz="xs">
                        {pos.serviceParentId && '  '}
                        {pos.serviceName}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="xs">
                        {pos.intervalMonths === 1 && 'Monthly'}
                        {pos.intervalMonths === 3 && 'Quarterly'}
                        {pos.intervalMonths === 6 && 'Biannually'}
                        {pos.intervalMonths === 12 && 'Annually'}
                        {![1, 3, 6, 12].includes(pos.intervalMonths) &&
                          `${pos.intervalMonths}mo`}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="xs">
                        {Number(pos.renewalPrice).toFixed(2)}{' '}
                        {pos.renewalCurrency}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="xs">
                        {pos.offsetDays ? `${pos.offsetDays}d` : '-'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz="xs">
                        {pos.fineType === 'static' &&
                          `${Number(pos.fineAmount).toFixed(2)} (static)`}
                        {pos.fineType === 'incremental' &&
                          `${Number(pos.fineAmount).toFixed(2)} + ${Number(pos.incrementValue).toFixed(2)}/${pos.incrementInterval}`}
                        {!pos.fineType && '-'}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {positions.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text fz="xs" c="dimmed" ta="center">
                        No positions
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>

            {history.length > 0 && (
              <>
                <Divider label="Audit Trail" labelPosition="left" />
                <Timeline bulletSize={16} lineWidth={1}>
                  {history.map((entry) => (
                    <Timeline.Item key={entry.id}>
                      <HistoryEntry entry={entry} />
                      <Text fz="xs" c="dimmed">
                        {formatDateTime(entry.createdAt)}
                      </Text>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </>
            )}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}
